import type { ActivitySummary } from '@painpal/contracts';
import { formatPace } from '@painpal/core';

const MINUS = '−';
const monthFormat = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });
const weekdayFormat = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' });

const pad2 = (n: number): string => String(n).padStart(2, '0');

interface DateParts { year: number; month: string; day: number; weekday: string }

function parts(localDate: string): DateParts {
  const date = new Date(`${localDate}T00:00:00Z`);
  return {
    year: date.getUTCFullYear(),
    month: monthFormat.format(date).toUpperCase(),
    day: date.getUTCDate(),
    weekday: weekdayFormat.format(date).toUpperCase(),
  };
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 3600)}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;
}

export function formatMinutesSeconds(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  return s < 3600 ? `${Math.floor(s / 60)}:${pad2(s % 60)}` : formatDuration(s);
}

export function formatHoursMinutes(totalSeconds: number): string {
  const minutes = Math.max(0, Math.floor(totalSeconds / 60));
  return `${Math.floor(minutes / 60)}:${pad2(minutes % 60)}`;
}

export const PROCESSING_LABEL = 'PROCESSING…';
export const orDash = <T>(value: T | null, format: (value: T) => string): string => (value === null ? '—' : format(value));
export const formatKmValue = (meters: number): string => (meters / 1000).toFixed(1);
export const formatKm = (meters: number | null): string => orDash(meters, (m) => `${formatKmValue(m)} KM`);
export const formatWatts = (watts: number | null): string => orDash(watts, (w) => `${Math.round(w)} W`);

export const formatPaceKm = (secondsPerKm: number): string => `${formatPace(secondsPerKm)} /KM`;
export const formatInt = (n: number): string => Math.round(n).toLocaleString('en-US');
export const formatWhole = (n: number): string => String(Math.round(n));

export function formatSigned(n: number): string {
  const rounded = Math.round(n);
  if (rounded > 0) return `+${rounded}`;
  if (rounded < 0) return `${MINUS}${Math.abs(rounded)}`;
  return '0';
}

export function formatMonthDay(localDate: string): string {
  const p = parts(localDate);
  return `${p.month} ${p.day}`;
}

export function formatWeekdayMonthDay(localDate: string): string {
  const p = parts(localDate);
  return `${p.weekday} ${p.month} ${p.day}`;
}

export function formatDayMonth(localDate: string): string {
  const p = parts(localDate);
  return `${p.day} ${p.month}`;
}

export function formatWeekdayDayMonth(localDate: string): string {
  const p = parts(localDate);
  return `${p.weekday} ${p.day} ${p.month}`;
}

export function formatWeekdayDay(localDate: string): string {
  const p = parts(localDate);
  return `${p.weekday} ${p.day}`;
}

export function formatDayMonthYear(localDate: string): string {
  const p = parts(localDate);
  return `${p.day} ${p.month} ${p.year}`;
}

export const weekdayLetter = (localDate: string): string => parts(localDate).weekday.charAt(0);

export const weekdayName = (localDate: string): string => parts(localDate).weekday;

export function formatTimeOfDay(iso: string): string {
  const date = new Date(iso);
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function formatRelative(iso: string, now: Date): string {
  const minutes = Math.max(0, Math.round((now.getTime() - Date.parse(iso)) / 60000));
  if (minutes < 60) return `${minutes}M AGO`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}H AGO`;
  return `${Math.round(hours / 24)}D AGO`;
}

export function splitHeadline(name: string): [string, string] {
  const words = name.trim().toUpperCase().split(/\s+/);
  const last = words.slice(-1).join('').replace(/\.$/, '');
  return [words.slice(0, -1).join(' '), `${last}.`];
}

export function noLoadLabel(activity: Pick<ActivitySummary, 'noLoadReason' | 'sport' | 'weightedSpeed'>): string {
  switch (activity.noLoadReason) {
    case 'no-power':
      return 'NO POWER DATA';
    case 'no-ftp':
      return 'NO FTP SET';
    case 'no-hr':
      return 'NO LOAD · NO HR';
    case 'no-threshold':
      return activity.sport === 'run' && activity.weightedSpeed !== null ? 'NO LOAD · SET THRESHOLD PACE' : 'NO LOAD · SET THRESHOLD HR';
    // Only rows stored before sports, until their one-off reprocess rewrites them.
    case 'sport-unsupported':
      return `NO LOAD · ${activity.sport.toUpperCase()}`;
    case null:
      return 'NO LOAD';
  }
}
